import { useMutation, type UseMutationResult } from '@tanstack/react-query'
import { apiClient, authHeader } from '../lib/api'
import { useAuth } from './useAuth'

export interface StudentExportResult {
  exportedCount: number
  requestedCount: number
}

/** Date.toISOString() is always UTC; the filename should reflect the recruiter's own day. */
function formatLocalDate(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/** responseType: 'blob' means error bodies also arrive as a Blob, not JSON. */
async function parseBlobError(blob: unknown): Promise<string> {
  try {
    const text = await (blob as Blob).text()
    const parsed = JSON.parse(text)
    return parsed?.error ?? 'Export failed. Please try again.'
  } catch {
    return 'Export failed. Please try again.'
  }
}

/**
 * Posts the selected student ids to /students/export and triggers a browser
 * download of the returned CSV.
 */
export function useStudentExport(): UseMutationResult<StudentExportResult, Error, string[]> {
  const { getAccessToken } = useAuth()

  return useMutation<StudentExportResult, Error, string[]>({
    mutationFn: async (studentIds: string[]) => {
      const token = await getAccessToken()

      try {
        const response = await apiClient.post(
          '/students/export',
          { studentIds },
          { ...authHeader(token), responseType: 'blob' }
        )

        const exportedCount = Number(response.headers['x-exported-count'] ?? studentIds.length)

        const url = URL.createObjectURL(response.data as Blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `athletes-export-${formatLocalDate(new Date())}.csv`
        document.body.appendChild(link)
        link.click()
        link.remove()
        URL.revokeObjectURL(url)

        return { exportedCount, requestedCount: studentIds.length }
      } catch (err: any) {
        if (err?.response?.data instanceof Blob) {
          throw new Error(await parseBlobError(err.response.data))
        }
        throw err instanceof Error ? err : new Error('Export failed. Please try again.')
      }
    },
  })
}
